import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { XIcon as X, CheckIcon as Check, ProhibitIcon as Prohibit, ArrowCounterClockwiseIcon as Reopen, CircleNotchIcon as Spinner, WarningIcon as Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { bulkDisposition } from "@/lib/api";
import { STALE_CLASS, objectLabel, stateClass, stateLabel } from "@/lib/dispositionConstants";
import {
  STALE_INCLUDE_WARNING, buildBulkBody, bulkActionsFor, bulkCountsLine, bulkExclusionBreakdown,
  bulkResultLink, bulkSuccessMessage, canSubmitBulk,
} from "@/lib/bulkDecision";

const ACTION_META = {
  validate: { label: "Validate", Icon: Check, cls: "ml-btn-primary" },
  reject: { label: "Reject", Icon: Prohibit, cls: "ml-btn-secondary" },
  reopen: { label: "Reopen", Icon: Reopen, cls: "ml-btn-secondary" },
};

/** Right-column panel: preview + one note + validate / reject / reopen every eligible row of a selection. */
export default function BulkDecisionPanel({ workspaceId, selection, onClose, onDone }) {
  const navigate = useNavigate();
  const isCell = selection.type === "matrix_cell";
  const [state, setState] = useState(isCell ? "proposed" : selection.filters?.state || "");
  const [includeStale, setIncludeStale] = useState(false);
  const [includeManual, setIncludeManual] = useState(false);
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const actions = useMemo(() => bulkActionsFor(isCell ? state : selection.filters?.state), [isCell, state, selection]);
  const previewAction = actions[0];

  const load = useCallback(() => {
    setLoading(true);
    return bulkDisposition(workspaceId, buildBulkBody({ selection, action: previewAction, state, includeStale, includeManual, note: "", dryRun: true }))
      .then(setPreview)
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not preview the selection."))
      .finally(() => setLoading(false));
  }, [workspaceId, selection, previewAction, state, includeStale, includeManual]);

  useEffect(() => { load(); }, [load]);

  const run = async (action) => {
    setBusy(action);
    try {
      const res = await bulkDisposition(workspaceId, buildBulkBody({ selection, action, state, includeStale, includeManual, note, dryRun: false }));
      const link = bulkResultLink(workspaceId, selection, action);
      if (res.affected_count > 0) {
        toast.success(bulkSuccessMessage(res), { duration: 9000, action: { label: "View result", onClick: () => navigate(link) } });
      } else {
        toast.message(res.message);
      }
      onDone?.(res, link);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk decision failed.");
    } finally {
      setBusy(null);
    }
  };

  const eligible = preview?.eligible_count || 0;
  const breakdown = bulkExclusionBreakdown(preview);
  const staleIncluded = includeStale && (preview?.preview_rows || []).some((r) => r.stale && !r.excluded_reason);

  return (
    <aside className="ml-card p-4 space-y-4" data-testid="bulk-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Bulk decision</div>
          <h3 className="text-sm font-semibold" data-testid="bulk-panel-title">
            {isCell ? <><span className="ml-mono">{selection.treatment}</span> × <span className="ml-mono">{selection.disposition}</span></> : "Filtered selection"}
          </h3>
          {!isCell && (
            <p className="text-[11px] text-[var(--ml-text-secondary)]" data-testid="bulk-filter-summary">
              {Object.entries(selection.filters || {}).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(" · ") || "all rows"}
            </p>
          )}
        </div>
        <button onClick={onClose} className="p-1 text-[var(--ml-text-secondary)] hover:text-[var(--ml-text)]" aria-label="Close" data-testid="bulk-panel-close"><X size={16} /></button>
      </div>

      {isCell && (
        <label className="block text-xs space-y-1">
          <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Rows in state</span>
          <select className="ml-input" value={state} onChange={(e) => setState(e.target.value)} data-testid="bulk-state-select">
            <option value="proposed">Proposed</option>
            <option value="validated">Validated</option>
            <option value="rejected">Rejected</option>
          </select>
        </label>
      )}

      <div className="border border-[var(--ml-border)] p-3 space-y-2 text-xs" data-testid="bulk-counts">
        {loading && !preview ? (
          <div className="flex items-center gap-2 text-[var(--ml-text-secondary)]"><Spinner size={14} className="animate-spin" /> Previewing…</div>
        ) : (
          <>
            <div className="flex items-baseline gap-3">
              <span className="text-2xl font-semibold tabular-nums" data-testid="bulk-affected">{eligible}</span>
              <span className="text-[var(--ml-text-secondary)]">row{eligible === 1 ? "" : "s"} will be affected</span>
              {loading && <Spinner size={12} className="animate-spin ml-auto" />}
            </div>
            <div className="text-[var(--ml-text-secondary)]" data-testid="bulk-requested">{bulkCountsLine(preview)}</div>
            {breakdown.length > 0 && (
              <ul className="space-y-0.5" data-testid="bulk-excluded">
                {breakdown.map(([key, n, label]) => (
                  <li key={key} className="flex justify-between gap-2" data-testid={`bulk-excluded-${key}`}>
                    <span className="text-[var(--ml-text-secondary)]">excluded · {label}</span>
                    <span className="tabular-nums font-medium">{n}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {actions.includes("validate") && (
        <div className="space-y-1.5 text-xs">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={includeStale} onChange={(e) => setIncludeStale(e.target.checked)} data-testid="bulk-include-stale" />
            Include stale rows{preview?.excluded_stale_count ? ` (${preview.excluded_stale_count} excluded now)` : ""}
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={includeManual} onChange={(e) => setIncludeManual(e.target.checked)} data-testid="bulk-include-manual" />
            Include manual rows{preview?.excluded_manual_count ? ` (${preview.excluded_manual_count} excluded now)` : ""}
          </label>
          {staleIncluded && (
            <div className={`flex items-start gap-2 border px-2.5 py-2 ${STALE_CLASS}`} data-testid="bulk-stale-warning">
              <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
              <span><strong>{STALE_INCLUDE_WARNING}</strong> Validating them records a decision on evidence that has changed.</span>
            </div>
          )}
        </div>
      )}

      {preview?.preview_rows?.length > 0 && (
        <div className="space-y-1">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            Rows{preview.preview_truncated ? ` (first ${preview.preview_rows.length} of ${preview.requested_count})` : ""}
          </div>
          <ul className="max-h-56 overflow-y-auto divide-y divide-[var(--ml-border)] border border-[var(--ml-border)] text-xs" data-testid="bulk-preview-list">
            {preview.preview_rows.map((r) => (
              <li key={r.id} className={`flex items-center gap-2 px-2 py-1 ${r.excluded_reason ? "opacity-60" : ""}`} data-testid={`bulk-preview-row-${r.id}`} data-excluded={r.excluded_reason || ""}>
                <span className="ml-mono truncate">{objectLabel(r)}</span>
                <span className={`ml-auto ml-mono text-[9px] uppercase tracking-widest px-1 border ${stateClass(r.state)}`}>{stateLabel(r.state)}</span>
                {r.stale && <span className={`ml-mono text-[9px] uppercase tracking-widest px-1 border ${STALE_CLASS}`}>stale</span>}
                {r.excluded_reason && <span className="text-[10px] text-[var(--ml-text-secondary)]">skip · {r.excluded_reason.replace("_", " ")}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <label className="block text-xs space-y-1">
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          {actions.includes("reopen") ? "Note (optional)" : "Decision note / rejection reason (required)"}
        </span>
        <textarea className="ml-input min-h-[72px]" value={note} onChange={(e) => setNote(e.target.value)} data-testid="bulk-note"
                  placeholder={actions.includes("reopen") ? "Why these rows are reopened" : "Applied verbatim to every affected row"} />
      </label>

      <div className="flex flex-wrap gap-2">
        {actions.map((a) => {
          const { label, Icon, cls } = ACTION_META[a];
          return (
            <button key={a} className={`${cls} flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed`} disabled={!!busy || loading || !canSubmitBulk(a, note, eligible)}
                    onClick={() => run(a)} data-testid={`bulk-${a}-btn`}>
              {busy === a ? <Spinner size={14} className="animate-spin" /> : <Icon size={14} weight="bold" />} {label} {eligible} selected
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-[var(--ml-text-secondary)]">
        Each affected row gets the same note, your name and a timestamp. Rejected rows are never re-decided here — reopen them first.
      </p>
    </aside>
  );
}
